import { ExtractDispatchValue } from "@TmpApp/types";
import { useState } from "react";
import { TUseAsyncCallback, TUseAsyncOptions, TUseAsyncStatus } from ".";
import { useDebounce, useIsMounted } from "..";

export default function useAsync<TData = any, TError = any>(
    callback: TUseAsyncCallback<TData>,
    dependencies: React.DependencyList = [],
    {
        autoRun = true,
        finally: finallyCallback = () => {},
        validate,
        debounceDelay = 0,
    }: TUseAsyncOptions = {}
) {
    const isMounted = useIsMounted();
    const [status, setStatus] = useState<TUseAsyncStatus>("idle");
    const [error, setError] = useState<TError>();
    const [value, setValue] = useState<TData>();

    const dispatch = <TDispatch extends React.Dispatch<React.SetStateAction<any>>>(
        value: ExtractDispatchValue<TDispatch>,
        dispatcher: TDispatch
    ) => {
        if (!isMounted()) {
            return;
        }

        dispatcher(value);
    };

    const run = () => {
        runWithCallack(callback);
    };

    const runWithCallack = (cb: TUseAsyncCallback<TData>) => {
        setError(undefined);
        setValue(undefined);
        if (validate && !validate()) {
            setStatus("idle");
            return;
        }
        setStatus("loading");
        cb(status)
            ?.then((response) => {
                dispatch(response, setValue);
                dispatch("success", setStatus);
            })
            .catch((response) => {
                dispatch(response, setError);
                dispatch("error", setStatus);
            })
            .finally(() => {
                finallyCallback?.();
            });
    };

    useDebounce(
        () => {
            run();
        },
        debounceDelay,
        dependencies,
        { autoRun: autoRun }
    );

    return { status, error, value, run, runWithCallack };
}
